import React, { useState } from 'react';
import { useRealtime } from '../../contexts/RealtimeContext';
import StoreManagement from '../../components/coaching/StoreManagement';
import OrderManagement from '../../components/coaching/OrderManagement';
import { ShoppingBag, ClipboardList, Package, TrendingUp } from 'lucide-react';

const StoreManagementPage = () => {
  const { userProfile, loading } = useRealtime();
  const [activeTab, setActiveTab] = useState('inventory');
  
  // Show loading state if profile is still being fetched
  if (loading && !userProfile) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Get coaching ID from user profile
  const coachingId = userProfile?.coachingId;

  if (!coachingId) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          <h3 className="font-bold">Access Denied</h3>
          <p>No coaching profile found. Please make sure you are logged in with a coaching admin account.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white flex items-center gap-3">
          <div className="p-2 bg-blue-600 rounded-lg text-white">
            <ShoppingBag size={28} />
          </div>
          Coaching Store
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400 max-w-2xl">
          Manage your institution's inventory and track student orders seamlessly. 
          Everything from uniforms to study materials in one place.
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-4 mb-8 border-b border-gray-200 dark:border-gray-700 overflow-x-auto pb-1">
        <button
          onClick={() => setActiveTab('inventory')}
          className={`flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all relative ${
            activeTab === 'inventory'
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <Package size={20} />
          Inventory Management
          {activeTab === 'inventory' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
          )}
        </button>
        <button
          onClick={() => setActiveTab('orders')}
          className={`flex items-center gap-2 px-6 py-3 text-sm font-bold transition-all relative ${
            activeTab === 'orders'
              ? 'text-blue-600 dark:text-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <ClipboardList size={20} />
          Order Tracking
          {activeTab === 'orders' && (
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-400 rounded-full" />
          )}
        </button>
      </div>

      {/* Conditional Rendering */}
      <div className="transition-all duration-300">
        {activeTab === 'inventory' ? (
          <StoreManagement coachingId={coachingId} />
        ) : (
          <OrderManagement coachingId={coachingId} />
        )}
      </div>
    </div>
  );
};

export default StoreManagementPage;
